import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "utils/cn";
import { useAppDispatch } from "store";
import { markItemsRead } from "store/notificationsSlice";
import { notificationsService } from "api/notifications";
import { INotificationItem } from "api/notifications/notifications.type";
import { resolveNotificationDisplay, getNotificationIcon, getNotificationRoute, getNotificationDownloadUrl, getNotificationImageUrl } from "./notificationDisplay";
import { formatRelativeTime } from "./whatsNew.data";
import { downloadFile } from "utils/downloadFile";
import { useSnackbar } from "hooks/useSnackbar";

interface NotificationItemProps {
  item: INotificationItem;
}

// Single row in the Notifications tab: icon + resolved title/body + optional cover thumbnail +
// relative timestamp + an unread dot. Row click marks the item read (optimistic) and navigates via
// the resolver's route, when there is one.
const NotificationItem = ({ item }: NotificationItemProps) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showError } = useSnackbar();
  const isUnread = item.read_at == null;
  // REF-6509: a cover that fails to LOAD must fall back to the family icon, not to a broken
  // image box. The URL is persisted at write time and never re-validated, so it can outlive the
  // object it points at (a deleted result, a bucket change) — and a viewer-resolved trial
  // watermark path can 404 on its own. `cover_url` being non-empty is not proof it renders.
  //
  // The state is the FAILED URL, not a boolean: a coalescing row keeps its id and swaps its
  // cover as later results land (`_BUMP_SQL` moves `cover_url`; Redux replaces the item under
  // the same id without remounting). A boolean would let one dead thumbnail hide every valid
  // one that followed for the life of the panel.
  const [failedCoverUrl, setFailedCoverUrl] = React.useState<string | null>(null);
  // REF-6607: a comment mention shows the mentioning teammate's photo (round) in the cover slot.
  const coverUrl = getNotificationImageUrl(item);
  const isAvatar = !item.cover_url && !!coverUrl;
  const coverFailed = !!coverUrl && failedCoverUrl === coverUrl;
  const { title, body, tone } = resolveNotificationDisplay(item, t);
  const Icon = getNotificationIcon(item);

  const toneClasses = tone === "error" ? "bg-red/10 text-red" : tone === "warning" ? "bg-orange/10 text-orange" : "bg-black-200 text-trueBlack dark:text-white";

  const handleClick = () => {
    if (isUnread) {
      dispatch(markItemsRead([item.id]));
      notificationsService.markRead([item.id]).catch(() => undefined);
    }

    // REF-6306 — a download-kind notification with a download_url downloads the file instead of
    // navigating (there is no route for it either way — see getNotificationRoute's `download` case).
    const downloadUrl = getNotificationDownloadUrl(item);
    if (downloadUrl) {
      downloadFile(downloadUrl).catch(() => showError(t("notifications:download.failed")));

      return;
    }

    const route = getNotificationRoute(item);
    if (!route) return;

    navigate(route, { state: { fromNotification: item.id } });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        "group relative flex w-full cursor-pointer items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-black-200/60",
        isUnread && "bg-black-100/60 dark:bg-black-800/40"
      )}
    >
      {coverUrl && !coverFailed ? (
        <img src={coverUrl} alt="" onError={() => setFailedCoverUrl(coverUrl)} className={cn("h-10 w-10 flex-shrink-0 object-cover bg-black-200", isAvatar ? "rounded-full" : "rounded-lg")} />
      ) : (
        <span className={cn("flex-center h-10 w-10 flex-shrink-0 rounded-lg", toneClasses)}>
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className={cn("text-[14px] text-trueBlack dark:text-white", isUnread ? "font-semibold" : "font-medium")}>{title}</span>
          {isUnread && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#FF3B30]" />}
        </div>
        {body && <span className="custom-line-clamp-2 text-[13px] text-grey">{body}</span>}
        <span className="text-[12px] text-grey/80">{formatRelativeTime(item.last_event_at || item.created_at || "", i18nInstance.language)}</span>
      </div>
    </div>
  );
};

export default NotificationItem;
